import { AlertTriangleIcon, InboxIcon, RotateCcwIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import type { AsortiCell } from '@app/components/asorti-grid'
import { Badge } from '@app/components/ui/badge'
import { Button } from '@app/components/ui/button'
import { TableCell, TableRow } from '@app/components/ui/table'
import { cn } from '@app/lib/cn'
import { percentText } from './format'
import type { OrderShipStatus, OrderSizeCell, OrderStatus, OrderType } from './types'

/* ── Badges ───────────────────────────────────────────────────────────────── */

const SHIP_STATUS_LABEL: Record<OrderShipStatus, string> = {
  pending: 'Bekliyor',
  partial: 'Kısmi',
  complete: 'Sevk edildi',
}

const SHIP_STATUS_TONE = {
  pending: 'neutral',
  partial: 'info',
  complete: 'ok',
} as const

export function ShipStatusBadge({
  status,
  overdue = false,
}: {
  status: OrderShipStatus
  overdue?: boolean
}) {
  if (overdue && status !== 'complete') {
    return <Badge tone="warn">{SHIP_STATUS_LABEL[status]} · gecikti</Badge>
  }
  return <Badge tone={SHIP_STATUS_TONE[status]}>{SHIP_STATUS_LABEL[status]}</Badge>
}

const TYPE_LABEL: Record<OrderType, string> = {
  S: 'Satış',
  A: 'Satınalma',
  ST: 'Numune',
}

/**
 * Korgun `SipTip`. ST records are never shipped and never land in production;
 * per the mirror's behavior, labeled as a sample/quote document.
 */
export function TypeBadge({ type, typeName }: { type: OrderType; typeName?: string }) {
  return (
    <Badge tone={type === 'A' ? 'info' : 'neutral'} title={`Korgun SipTip: ${type}`}>
      {typeName ?? TYPE_LABEL[type]}
    </Badge>
  )
}

export function StatusBadge({ status }: { status: OrderStatus }) {
  return status === 'closed' ? (
    <Badge tone="neutral" title="Korgun Durum = '*' (kapatılmış kayıt)">
      Kapalı
    </Badge>
  ) : (
    <Badge tone="accent">Açık</Badge>
  )
}

/**
 * Compact sample-order (Korgun `SipTip = 'ST'`) marker for the order-number
 * cell. The Tip column that would normally carry the "Numune" badge hides
 * below `xl` (see COLUMN.type in siparisler.tsx), so at narrower widths an ST
 * row otherwise looks identical to a sales order — same zero-shipped
 * columns, same layout. This dot rides next to the order number at every
 * width instead: small enough not to compete with the number, present
 * everywhere the badge isn't.
 *
 * `role="img"` is load-bearing, not decoration: a bare `<span>` has the
 * implicit ARIA `generic` role, on which `aria-label` is prohibited and
 * would be dropped from the accessible name — `role="img"` is what makes
 * "Numune" the element's announced name instead of the literal "N".
 */
export function SampleDot() {
  return (
    <span
      role="img"
      aria-label="Numune"
      title="Numune"
      className="inline-flex size-3.5 shrink-0 items-center justify-center rounded-full bg-ink-muted/12 text-[0.5rem] font-bold leading-none text-ink-muted"
    >
      N
    </span>
  )
}

/* ── Shipment bar ─────────────────────────────────────────────────────────── */

export interface ShipBarProps {
  percent: number
  status: OrderShipStatus
  overdue?: boolean
  /** Show the percentage in mono next to the bar. */
  showLabel?: boolean
  className?: string
}

/**
 * Thin progress bar: shipped / ordered. Kordovan is reserved for primary
 * actions only, so fill uses the semantic status colors.
 */
export function ShipBar({ percent, status, overdue = false, showLabel = true, className }: ShipBarProps) {
  const fill =
    status === 'complete' ? 'bg-zeytin' : overdue ? 'bg-hardal' : status === 'partial' ? 'bg-celik' : 'bg-ink-muted/35'

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div
        className="h-1 min-w-10 flex-1 overflow-hidden rounded-xs bg-hairline"
        role="img"
        aria-label={`Sevkiyat ${percentText(percent)}`}
      >
        <div className={cn('h-full', fill)} style={{ width: `${Math.max(percent, percent > 0 ? 4 : 0)}%` }} />
      </div>
      {showLabel ? (
        <span
          aria-hidden
          className={cn(
            'w-9 shrink-0 text-right font-mono text-micro tnum',
            status === 'complete' ? 'text-zeytin' : overdue ? 'text-hardal' : 'text-ink-muted',
          )}
        >
          {/* A single carton shipped on a thousand-pair order rounds to %0 — don't hide that. */}
          {percent === 0 && status === 'partial' ? '<%1' : percentText(percent)}
        </span>
      ) : null}
    </div>
  )
}

/* ── Due date ─────────────────────────────────────────────────────────────── */

export function DueDateCell({
  dateText,
  overdueDays,
}: {
  dateText: string
  overdueDays: number | null
}) {
  if (overdueDays === null) {
    return <span className="font-mono tnum text-ink-muted">{dateText}</span>
  }
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span className="font-mono tnum font-medium text-hardal">{dateText}</span>
      <span className="rounded-xs bg-hardal/10 px-1 font-mono text-micro tnum text-hardal">
        +{overdueDays} g
      </span>
    </span>
  )
}

/* ── Asorti cells ─────────────────────────────────────────────────────────── */

/**
 * Converts a size breakdown into `<AsortiGrid>` cells. The status tone tells
 * the shipment story: a fully-shipped size is olive, a partially-shipped size
 * is steel, an unshipped size stays neutral.
 */
export function toAsortiCells(sizes: readonly OrderSizeCell[]): AsortiCell[] {
  return sizes.map((s) => ({
    size: s.size,
    qty: s.ordered,
    status: s.ordered > 0 && s.kalan === 0 ? 'ok' : s.shipped > 0 ? 'info' : 'neutral',
  }))
}

/* ── State screens ────────────────────────────────────────────────────────── */

export function TableSkeleton({ rows = 8, cols = 8 }: { rows?: number; cols?: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, i) => (
        <TableRow key={i} aria-hidden>
          {Array.from({ length: cols }, (__, j) => (
            <TableCell key={j}>
              <span
                className="block h-2.5 animate-pulse rounded-xs bg-hairline"
                style={{ width: `${[70, 55, 90, 45, 60, 50, 40, 65][j % 8]}%` }}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-14 text-center">
      <InboxIcon className="size-5 text-ink-muted/60" aria-hidden />
      <p className="font-display text-[0.9375rem] font-semibold text-ink">{title}</p>
      <p className="max-w-sm text-data text-ink-muted">{description}</p>
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  )
}

/**
 * Failed load. `action` replaces the retry button for errors retrying cannot
 * fix — a 404 is the server's final answer, so the detail screens hand in a
 * "back to the list" link instead of a button that would fail again.
 */
export function ErrorState({
  title = 'Veriler yüklenemedi',
  message,
  onRetry,
  action,
}: {
  title?: string
  message: string
  onRetry?: () => void
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-14 text-center">
      <AlertTriangleIcon className="size-5 text-kordovan" aria-hidden />
      <p className="font-display text-[0.9375rem] font-semibold text-ink">{title}</p>
      <p className="max-w-sm text-data text-ink-muted">{message}</p>
      {action ? (
        <div className="mt-1">{action}</div>
      ) : onRetry ? (
        <Button variant="outline" className="mt-1" onClick={onRetry}>
          <RotateCcwIcon aria-hidden />
          Yeniden dene
        </Button>
      ) : null}
    </div>
  )
}
